import { GeoJsonLayer } from '@deck.gl/layers/typed';
import { parseSync } from '@loaders.gl/core';
import { StrictPoint } from 'ais-facade-api/api';
import { useGetSimpleRouteMutation } from 'app/api/facade';
import { useGetShipInfoQuery } from 'app/api/ships';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { useEffect, useMemo, useState } from 'react';
import { WKTLoader } from '@loaders.gl/wkt';
import { PathStyleExtension } from '@deck.gl/extensions/typed';
import { setPredictedRoute } from 'app/utils/selectedTargetsSlice';
import { useAlertHook } from '../../../../hooks/useAlertHook';

export const useLayerRoutePredicted = (isHistoryMode?: boolean) => {
  const dispatch = useAppDispatch();
  const { addAlert } = useAlertHook();
  const mode = useAppSelector((state) => state.mapStateSlice.mode);
  const predictedRoute = useAppSelector((state) => state.selectedTargetsSlice?.predictedRoute);
  const selectedTarget = useAppSelector((state) => state.selectedTargetsSlice?.selectedTarget);
  const isNotBaseMode = mode === 'analytics' || mode === 'playback' || mode === 'snapshot'

  const { data: ship, ...shipInfoRequest } = useGetShipInfoQuery(selectedTarget?.target?.object?.mmsi, {
    skip: !selectedTarget?.target?.object?.mmsi || isHistoryMode,
  });

  const [getRoute, { data: routeData, ...routeRequest }] = useGetSimpleRouteMutation();
  const prediction = ship?.result?.destinationPorts;

  useEffect(() => {
    if (isHistoryMode) {
      dispatch(setPredictedRoute(null));
    }
  }, [isHistoryMode]);

  useEffect(() => {
    dispatch(setPredictedRoute(null));
  }, [selectedTarget]);

  const shipPosition: StrictPoint | undefined = useMemo(() => {
    if (shipInfoRequest.isFetching || shipInfoRequest.isLoading || !ship?.result?.position || selectedTarget?.type !== 'vessel')
      return;

    const { longitude, latitude } = ship.result.position;
    if (typeof longitude === 'number' && typeof latitude === 'number')
      return {
        longitude,
        latitude,
      };
  }, [selectedTarget, ship]);

  useEffect(() => {
    if (!isHistoryMode && prediction?.length === 1 && shipPosition) {
      const { lat, lon } = prediction[0];
      if (typeof lat === 'number' && typeof lon === 'number' && predictedRoute.isShown)
        getRoute({
          from: shipPosition,
          to: {
            longitude: lon,
            latitude: lat,
          },
        });
    } else {
      dispatch(setPredictedRoute(null));
    }
  }, [prediction, selectedTarget, predictedRoute.isShown]);

  const [geometry, setGeometry] = useState<any>();

  useEffect(() => {
    const warningMessage = routeData?.messages?.find((message) => message.type === 'warning');
    if (warningMessage) {
      addAlert({
        data: {
          messages: [{ description: warningMessage.description, type: warningMessage.type, code: warningMessage.code }],
        },
      });
    } else if (routeData?.route) {
      dispatch(setPredictedRoute({ ...routeData.route }));
    }
  }, [routeData]);

  useEffect(() => {
    if (predictedRoute?.route?.geometry) {
      const geometry = parseSync(predictedRoute.route.geometry, WKTLoader);
      setGeometry(geometry);
    }
  }, [predictedRoute]);

  const pathStyleExtension = new PathStyleExtension({ dash: true });

  const routeLayer = new GeoJsonLayer({
    id: 'layer-route-predicted',
    visible: predictedRoute?.isShown && !routeRequest.isLoading && Boolean(predictedRoute.route) && !isNotBaseMode,
    data: geometry,
    stroked: true,
    getLineColor: () => {
      const lineColor: [number, number, number, number] = [255, 92, 0, 255];

      return lineColor;
    },
    getLineWidth: 1,
    extensions: [pathStyleExtension],
    getDashArray: [10, 5],
    lineWidthUnits: 'pixels',
  });

  return routeLayer;
};
